// QuizView — extracted from App.js (Feb 2026 refactor).
// Rendered within a Layout that is passed via prop by the wrapper in App.js.

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, AlertCircle } from "lucide-react";

import ModuleCompleteModal from "../components/ModuleCompleteModal";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const QuizView = ({ moduleId, onBack, Layout }) => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [fullResults, setFullResults] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  useEffect(() => {
    const loadQuiz = async () => {
      try {
        const response = await axios.get(`${API}/modules/${moduleId}/quiz`, getAuthHeader());
        setQuestions(response.data);
      } catch (error) {
        toast.error("Fehler beim Laden des Quiz");
      }
      setLoading(false);
    };
    loadQuiz();
  }, [moduleId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const selectAnswer = (questionId, optionIndex) => {
    if (result) return;
    setAnswers({ ...answers, [questionId]: optionIndex });
  };

  const submitQuiz = async () => {
    if (Object.keys(answers).length < questions.length) {
      toast.error("Bitte beantworten Sie alle Fragen");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/modules/${moduleId}/quiz/submit`,
        { module_id: moduleId, answers },
        getAuthHeader()
      );
      setResult(response.data);

      const resultsResponse = await axios.get(
        `${API}/modules/${moduleId}/quiz/results`,
        getAuthHeader()
      );
      setFullResults(resultsResponse.data.questions);

      if (response.data.passed) {
        setShowCelebration(true);
        setShowSuccessModal(true);
        toast.success("Herzlichen Glückwunsch! Quiz bestanden!");
        setTimeout(() => setShowCelebration(false), 5000);
      } else {
        toast.error("Leider nicht bestanden. Versuchen Sie es erneut!");
      }
    } catch (error) {
      toast.error("Fehler beim Einreichen");
    }
    setSubmitting(false);
  };

  const getOptionClass = (question, optionIndex) => {
    if (!result) {
      return answers[question.id] === optionIndex ? "selected" : "";
    }
    const fullQ = fullResults?.find((q) => q.id === question.id);
    const isCorrect = fullQ?.correct_index === optionIndex;
    const wasSelected = answers[question.id] === optionIndex;
    if (isCorrect) return "correct";
    if (wasSelected && !isCorrect) return "incorrect";
    return "";
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-terra-dark" />
        </div>
      </Layout>
    );
  }

  if (questions.length === 0) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto animate-fade-in">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-sand-700 hover:text-terra-dark mb-6 transition-colors"
            data-testid="back-to-module"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück zum Modul
          </button>
          <div className="text-center py-16 px-6 bg-cream-100 border border-cream-300 rounded-2xl dh-botanical overflow-hidden" data-testid="quiz-empty-state">
            <div className="text-[11px] uppercase font-bold text-terra mb-3" style={{ letterSpacing: "0.3em" }}>Wissenstest · Kapitelende</div>
            <h1 className="font-display text-3xl text-espresso leading-tight">Der Wissenstest wird gerade <span className="italic text-terra">vorbereitet</span>.</h1>
            <p className="font-display italic text-sand-600 mt-3 text-[15px] max-w-md mx-auto">
              Für dieses Kapitel stehen die Prüfungsfragen noch nicht bereit. Schau in Kürze wieder vorbei — dein Fortschritt geht nicht verloren.
            </p>
            <div className="dh-palette-dots mt-7" aria-hidden>
              {['#E07A5F', '#8A8D7F', '#C9968D', '#E9A0AC', '#522A25', '#D89232'].map((c) => (
                <span key={c} style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto animate-fade-in">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sand-700 hover:text-terra-dark mb-6 transition-colors"
          data-testid="back-to-module"
        >
          <ArrowLeft className="w-4 h-4" />
          Zurück zum Modul
        </button>

        <div className="mb-8">
          <div className="text-[11px] uppercase font-bold text-terra mb-2" style={{ letterSpacing: "0.3em" }}>Wissenstest · Kapitelende</div>
          <h1 className="font-display text-3xl md:text-4xl text-espresso leading-tight">Zeig, was <span className="italic text-terra">sitzt</span>.</h1>
          <p className="font-display italic text-sand-600 mt-2 text-[15px]">
            Beantworte alle Fragen in Ruhe — du brauchst mindestens 70%.
          </p>
        </div>

        {result && (
          <div className="mb-8 p-2" style={{ backgroundColor: "#F5F0EA", border: "1px solid rgba(216,146,50,0.35)", borderRadius: 4 }}>
            <div className="px-6 py-6 text-center" style={{ border: "1px solid rgba(216,146,50,0.35)", borderRadius: 2 }}>
              {result.passed ? (
                <>
                  <h3 className="font-display text-[26px] text-espresso leading-snug">
                    Bestanden — das <span className="italic text-terra">sitzt</span>.
                  </h3>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] mt-3" style={{ color: "#8F650F" }}>
                    {result.correct_answers} von {result.total_questions} richtig · {Math.round(result.score_percentage)}%
                  </p>
                </>
              ) : (
                <>
                  <h3 className="font-display text-[26px] text-espresso leading-snug">
                    Fast — noch einmal <span className="italic text-terra">in Ruhe</span>.
                  </h3>
                  <p className="font-mono text-[11px] uppercase tracking-[0.2em] mt-3 text-sand-600">
                    {result.correct_answers} von {result.total_questions} richtig · {Math.round(result.score_percentage)}% — schau dir die Erklärungen unten an.
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        <div className="space-y-5">
          {questions.map((question, qIndex) => {
            const fullQ = fullResults?.find((q) => q.id === question.id);
            const userCorrect = result && fullQ && answers[question.id] === fullQ.correct_index;
            return (
            <div
              key={question.id}
              className="p-1.5"
              style={{ backgroundColor: "#F5F0EA", border: "1px solid rgba(216,146,50,0.3)", borderRadius: 4 }}
              data-testid={`question-${qIndex}`}
            >
              <div className="p-5 sm:p-6" style={{ border: "1px solid rgba(216,146,50,0.3)", borderRadius: 2 }}>
                <p className="font-mono text-[10px] uppercase font-semibold mb-2" style={{ letterSpacing: "0.22em", color: "#8F650F" }}>
                  Frage {qIndex + 1} von {questions.length}
                </p>
                <h3 className="font-display text-lg md:text-xl text-espresso leading-snug mb-5">
                  {question.question}
                </h3>

                <div className="space-y-2.5">
                  {question.options.map((option, oIndex) => (
                    <button
                      key={oIndex}
                      onClick={() => selectAnswer(question.id, oIndex)}
                      disabled={!!result}
                      className={`
                        w-full text-left p-3.5 rounded-xl border transition-all flex items-start gap-3
                        quiz-option ${getOptionClass(question, oIndex)}
                        disabled:cursor-default
                      `}
                      data-testid={`option-${qIndex}-${oIndex}`}
                    >
                      <span className="font-display text-lg text-terra leading-none flex-shrink-0 w-5 pt-0.5">
                        {String.fromCharCode(65 + oIndex)}
                      </span>
                      <span className="text-sand-800 text-sm sm:text-base">{option}</span>
                    </button>
                  ))}
                </div>

                {result && fullQ && (
                  userCorrect ? (
                    <div className="mt-4 flex items-start gap-2">
                      <span className="font-display italic text-[17px] leading-snug flex items-center gap-2" style={{ color: "#8F650F" }}>
                        ✓ Sitzt.
                      </span>
                      {fullQ.explanation && (
                        <p className="text-sm text-sand-600 leading-relaxed pt-0.5">{fullQ.explanation}</p>
                      )}
                    </div>
                  ) : (
                    <p className="mt-4 text-sm text-terra leading-relaxed">
                      <span className="font-display italic text-[16px]">Fast — schau nochmal:</span>{' '}
                      <strong className="font-semibold">{question.options[fullQ.correct_index]}</strong>
                      {fullQ.explanation ? ` — ${fullQ.explanation}` : ''}
                    </p>
                  )
                )}
              </div>
            </div>
            );
          })}
        </div>

        {!result && (
          <div className="mt-8 flex justify-center">
            <button
              onClick={submitQuiz}
              disabled={submitting || Object.keys(answers).length < questions.length}
              className="bg-terra hover:bg-terra-dark text-white rounded-full h-12 px-8 font-semibold inline-flex items-center gap-2 transition-colors shadow-soft hover:shadow-hover disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
              data-testid="submit-quiz"
            >
              {submitting ? "Wird eingereicht…" : "Quiz einreichen"}
            </button>
          </div>
        )}

        {result && (
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <button
              onClick={onBack}
              className="bg-cream-100 border border-cream-300 text-sand-800 hover:bg-cream-100 rounded-full h-11 px-6 font-semibold transition-colors"
            >
              Zurück zum Modul
            </button>
            {!result.passed && (
              <button
                onClick={() => { setResult(null); setAnswers({}); setFullResults(null); }}
                className="bg-terra hover:bg-terra-dark text-white rounded-full h-11 px-6 font-semibold transition-colors shadow-soft hover:shadow-hover"
              >
                Erneut versuchen
              </button>
            )}
          </div>
        )}

        {showSuccessModal && result?.passed && (
          <ModuleCompleteModal
            moduleId={moduleId}
            scorePercentage={result.score_percentage}
            getAuthHeader={getAuthHeader}
            onClose={() => setShowSuccessModal(false)}
            onNavigateNext={(nextModuleId) => {
              setShowSuccessModal(false);
              navigate(`/module/${nextModuleId}`);
            }}
            onShowCertificate={() => {
              setShowSuccessModal(false);
              navigate("/my-certificates");
            }}
          />
        )}
      </div>
    </Layout>
  );
};

export default QuizView;
